import { BasedModelUploadWarningDialog } from "@/components/DialogBox/BasedModelUploadWarningDialog";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { InsufficientCreditsDialog } from "@/components/DialogBox/InsufficientCreditDialog";
import { DragAndDropUpload } from "@/components/DragAndDropUpload/DragAndDropUpload";
import { AddSvg, CloseSvg, EditSvg, ErrorSvg, NoneSelectedSvg, WarningSvg } from "@/components/Icon/Icon";
import { Model } from "@/components/Model/Model";
import { StyledTooltip, TooltipBodyWithHeader } from "@/components/StyledTooltip/StyledTooltip";
import { Viewport, ViewportContainer, ViewportLoading } from "@/components/Viewport/Viewport";
import { designTableConstants } from "@/constants/designTableConstants";
import { useOrgCredits, useUploadsServiceCost } from "@/hooks/credits";
import { useOrg } from "@/hooks/org";
import {
  EditTemplateModal,
  TemplateModelAction
} from "@/pages/Home/DesignSection/AssembledTabsSection/EditTemplateModal/EditTemplateModal";
import { changeTemplate, TemplateSliceToCaseOptions } from "@/pages/Home/DesignSection/AssembledTabsSection/EditTemplateModal/utils";
import { ScansSection } from "@/pages/Home/DesignSection/AssembledTabsSection/UploadScansModal/ScansSection/ScansSection";
import { TemplateReadPublic, useGetTemplatesApiV1TemplatesGetQuery } from "@/store/baseClientApi";
import {
  clearCaseUploads,
  clearUploadSliceData,
  selectAreThumbnailsLoading,
  selectCaseName,
  selectCaseUploads,
  selectCurrentProcessingBatchSize,
  selectEmptyFolderError,
  selectIsInspecting,
  selectIsProcessing,
  selectIsUploading,
  selectLowerScan,
  selectNoScansDetectedError,
  selectNumCaseUploads,
  selectNumErrors,
  selectNumProcessed,
  selectNumUnprocessed,
  selectNumWarnings,
  selectScans,
  selectUpperScan,
  setAreUploadsSorted,
  setEmptyFolderError,
  setIsInspecting,
  setIsUploading,
  setNoScansDetectedError,
  uploadNewCase
} from "@/store/slices/casesSlice";
import { handleUploadCases } from "@/store/slices/caseUploadsSlice";
import { selectModalOpen, setModalOpen } from "@/store/slices/modalSlice";
import { defaultTemplate, selectTemplate, setTemplate } from "@/store/slices/templateSlice";
import { RootState, store } from "@/store/store";
import { theme } from "@/themes/theme";
import { DataObject } from "@/typed/CaseStateData";
import { DentalCase } from "@/typed/DentalCase";
import { rotationDegreesToEuler } from "@/utils/geometryUtils";
import { ensureError, localizeError } from "@/utils/helpers";
import { Store } from "@/utils/indexeddb";
import { EmptyError, NoScansDetectedError, processCaseInputDataTransfer, processCaseInputFiles } from "@/utils/processCaseInputFiles";
import { getUploadScansFromIndexedDB } from "@/utils/uploadsCache";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Theme,
  Typography
} from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { ReactElement, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Vector3 } from "three";

const selectInspecting = createSelector(
  (state: RootState) => state.cases.inspectingCaseIndex,
  (state: RootState) => state.cases.inspectingScanId,
  (inspectingCaseIndex, inspectingScanIndex) => ({
    caseId: inspectingCaseIndex,
    scanId: inspectingScanIndex
  })
);

const AddingCasesToastId = "addingCases";

const UploadToast = () => {
  const { t } = useTranslation("cases");
  const [prevBatchSize, setPrevBatchSize] = useState(0);
  const [prevNumCases, setPrevNumCases] = useState(0);
  const currentBatchSize = useSelector((state: RootState) => selectCurrentProcessingBatchSize(state));
  const numUnprocessed = useSelector((state: RootState) => selectNumUnprocessed(state));
  const numProcessed = useSelector((state: RootState) => selectNumProcessed(state));
  const numCases = useSelector((state: RootState) => selectNumCaseUploads(state));

  useEffect(() => {
    return () => {
      toast.remove("addingCases"); // Close toast when modal closes (uploads terminate in that case)
    };
  }, []);

  useEffect(() => {
    const numCasesRemainingToUpload = currentBatchSize - numUnprocessed;

    // Occasionally if clicked too fast after closing, the table hasn't been cleared yet and so will create a toast based on the previous
    //   number of uploaded cases
    if (numCases === 0) {
      toast.remove(AddingCasesToastId);
    } else if (numProcessed < numCases) {
      // Show/update loading toast
      toast.loading(t("label.addingCases_amount", { amount: numCasesRemainingToUpload, total: currentBatchSize }), {
        id: AddingCasesToastId
      });
    } else if (numProcessed === numCases && prevBatchSize !== 0) {
      if (prevNumCases > numCases && prevBatchSize === 1) {
        toast.dismiss(AddingCasesToastId); // no cases were added
        return;
      }

      // Replace loading toast with success message and auto-dismiss after 2 seconds
      toast.success(t("label.casesAdded", { amount: prevBatchSize, total: prevBatchSize }), {
        id: AddingCasesToastId,
        duration: 2000
      });
    }
    setPrevBatchSize(currentBatchSize);
    setPrevNumCases(numCases);
  }, [numUnprocessed, numProcessed, numCases, currentBatchSize, prevBatchSize, prevNumCases, t]);

  return null;
};

const ViewportModal = () => {
  const { t } = useTranslation();

  const { caseId, scanId } = useSelector((state: RootState) => selectInspecting(state));
  const modelName = useSelector((state: RootState) => selectCaseName(state, caseId ?? 0));
  const lowerScan = useSelector((state: RootState) => selectLowerScan(state, caseId ?? 0));
  const upperScan = useSelector((state: RootState) => selectUpperScan(state, caseId ?? 0));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [model, setModel] = useState<DataObject[] | undefined>();
  const [modelViewportError, setModelViewportError] = useState<boolean>(false);
  const open = useSelector((state: RootState) => selectIsInspecting(state));

  useEffect(() => {
    const fetchModelData = async () => {
      const scanIds = [lowerScan, upperScan].filter((id) => id !== undefined);

      if (open) {
        setModel(undefined);
        setModelViewportError(false);
        setIsLoading(true);
      }
      if (caseId == null) {
        return;
      }
      try {
        const caseState = await getUploadScansFromIndexedDB(caseId);
        if (!caseState || caseState.length === 0) {
          setModelViewportError(true);
          setIsLoading(false);
          return;
        } else {
          if (scanId === null) {
            setModel(caseState.filter((scan) => scanIds.includes(scan.id)));
          } else if (scanId != null) {
            setModel(caseState.filter((scan) => scan.id === scanId));
          }
        }
        setIsLoading(false);
      } catch (err) {
        setModelViewportError(true);
        setIsLoading(false);
        const error = ensureError(err);
        toast.error(localizeError(t, error));
      }
    };

    fetchModelData();
  }, [caseId, scanId, open, lowerScan, upperScan]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    store.dispatch(setIsInspecting({ isInspecting: false }));
  };

  return (
    <Box sx={{ width: "fit-content", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "white" }}>
      <UploadToast />
      <Modal open={open} onClose={handleClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "600px",
            maxHeight: "616px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            backgroundColor: "white",
            overflow: "hidden"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              padding: "1em",
              alignItems: "center"
            }}
          >
            <Typography noWrap variant="h1">
              {modelName}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                height: "32px",
                borderRadius: "100px"
              }}
            >
              <CloseSvg color="inherit" />
            </IconButton>
          </Box>
          <Divider sx={{ width: "100%" }} />

          {/* Body */}
          {!isLoading && (modelViewportError || caseId == null) ? (
            <ViewportContainer>
              <NoneSelectedSvg />
            </ViewportContainer>
          ) : model !== undefined ? (
            <Box
              sx={{
                width: "100%",
                height: "60vh",
                maxHeight: "488px",
                backgroundColor: (theme) => theme.surface.low
              }}
            >
              <Viewport
                title={modelName ?? "Unnamed"}
                maximisedFooter={
                  <Box
                    component="div"
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "flex-end",
                      gap: "6px"
                    }}
                  ></Box>
                }
              >
                <Model
                  fileData={model}
                  position={new Vector3(0, 0, 0)}
                  rotation={rotationDegreesToEuler(0, 0, 0)}
                  color={theme.model.model}
                />
              </Viewport>
            </Box>
          ) : (
            <ViewportLoading modelId={""} modelStore={Store.ScanStore} />
          )}
          <Divider sx={{ width: "100%" }} />

          {/* Footer */}
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", width: "100%", padding: "1em" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: "100px",
                height: "32px"
              }}
              onClick={handleClose}
            >
              {t("common:button.close")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

const TemplateErrorTooltip = ({
  children,
  templates,
  isLoading
}: {
  children: ReactElement;
  templates?: TemplateReadPublic[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation("template");
  if (isLoading || (templates !== undefined && templates.length !== 0)) {
    return <>{children}</>;
  }

  return (
    <StyledTooltip title={t("message.createATemplate")} placement="top">
      {children}
    </StyledTooltip>
  );
};

const TemplateControlBar = () => {
  const { t } = useTranslation("cases");

  const [openEditTemplateModal, setOpenEditTemplateModal] = useState(false);
  const [templateAction, setTemplateAction] = useState<TemplateModelAction>("edit");
  const { data: templates, isLoading } = useGetTemplatesApiV1TemplatesGetQuery(
    {
      pagination: false,
      sorting: "name:1"
    },
    { pollingInterval: designTableConstants.POLLING_INTERVAL, skipPollingIfUnfocused: true }
  );
  const noTemplates = templates?.content === undefined || templates.content.length === 0;
  const selectedTemplateId = useSelector((state: RootState) => selectTemplate(state).templateId);
  const selectedTemplateExists = templates?.content?.find((template) => template.id === selectedTemplateId) !== undefined;

  useEffect(() => {
    if (!openEditTemplateModal && selectedTemplateId === undefined && templates?.content !== undefined && templates.content.length > 0) {
      changeTemplate(templates.content[0]);
    } else if (!selectedTemplateExists && templates?.content !== undefined) {
      store.dispatch(setTemplate({ ...defaultTemplate }));
    }
  }, [openEditTemplateModal, selectedTemplateId, templates?.content, selectedTemplateExists]);

  const handleSelectDesignTemplate = (event: SelectChangeEvent<string>) => {
    const selectedTemplate = event.target.value; // Extract the selected label from the event

    const template = templates?.content?.find((template) => template.id.toString() === selectedTemplate);
    if (template !== undefined) {
      changeTemplate(template);
    } else {
      store.dispatch(setTemplate({ ...defaultTemplate }));
    }
  };

  const handleAddTemplate = () => {
    setOpenEditTemplateModal(true);
    setTemplateAction("add");
    store.dispatch(setTemplate({ ...defaultTemplate }));
  };

  const handleEditTemplate = () => {
    const template = templates?.content?.find((template) => template.id === selectedTemplateId);
    if (template === undefined) {
      return;
    }

    setOpenEditTemplateModal(true);
    setTemplateAction("edit");
    changeTemplate(template);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px"
      }}
    >
      <Typography>{t("uploadModal.text.designTemplate")}:</Typography>
      <TemplateErrorTooltip templates={templates?.content} isLoading={isLoading}>
        <Select
          value={templates?.content !== undefined ? (selectedTemplateId?.toString() ?? "") : ""}
          onChange={handleSelectDesignTemplate}
          displayEmpty
          sx={{
            width: "200px",
            borderRadius: "4px",
            height: "36px"
          }}
          error={noTemplates}
          disabled={isLoading}
        >
          {noTemplates && (
            <MenuItem disabled value="">
              {t("common:label.none")}
            </MenuItem>
          )}
          {templates?.content?.map((template) => (
            <MenuItem key={template.id} value={template.id.toString()}>
              {template.name}
            </MenuItem>
          ))}
        </Select>
      </TemplateErrorTooltip>
      <Box component="div">
        <IconButton
          onClick={handleEditTemplate}
          sx={{
            height: "40px",
            width: "40px",
            borderRadius: "100px"
          }}
          disabled={selectedTemplateId === undefined || isLoading}
        >
          <EditSvg color="inherit" />
        </IconButton>
        <IconButton
          onClick={handleAddTemplate}
          sx={{
            height: "40px",
            width: "40px",
            borderRadius: "100px"
          }}
          disabled={isLoading}
        >
          <AddSvg color="inherit" />
        </IconButton>
      </Box>
      {isLoading && <CircularProgress size={24} />}
      <EditTemplateModal open={openEditTemplateModal} setOpen={setOpenEditTemplateModal} action={templateAction} />
    </Box>
  );
};

const UploadModal = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation("cases");
  const { orgId } = useOrg();

  const open = useSelector((state: RootState) => selectModalOpen(state, UPLOAD_SCANS_MODAL));
  const { numCredits } = useOrgCredits();
  const { totalCost } = useUploadsServiceCost();
  const insufficientCreditsForUpload = numCredits !== undefined ? numCredits < totalCost : false;

  const numWarnings = useSelector((state: RootState) => selectNumWarnings(state));
  const numErrors = useSelector((state: RootState) => selectNumErrors(state));

  const hasCases = useSelector((state: RootState) => selectNumCaseUploads(state) !== 0);
  const hasTemplate = useSelector((state: RootState) => selectTemplate(state).templateId !== undefined);
  const isProcessing = useSelector(selectIsProcessing);
  const areThumbnailsLoading = useSelector(selectAreThumbnailsLoading);
  const hasScans = useSelector((state: RootState) => Object.values(selectScans(state)).filter((scan) => scan !== null).length !== 0);

  const isUploading = useSelector(selectIsUploading);
  const noScansDetectedError = useSelector(selectNoScansDetectedError);
  const emptyFolderError = useSelector(selectEmptyFolderError);
  const [quitWarning, setQuitWarning] = useState<boolean>(false);
  const [insufficientCreditsError, setInsufficientCreditsError] = useState<boolean>(false);

  const submitDisabled =
    isUploading || isProcessing || !hasCases || numErrors > 0 || orgId === undefined || !hasTemplate || areThumbnailsLoading || !hasScans;

  const close = async () => {
    store.dispatch(setModalOpen({ name: UPLOAD_SCANS_MODAL, value: false }));
    await store.dispatch(clearCaseUploads());
  };
  const handleClose = () => {
    if (hasCases || isProcessing) {
      setQuitWarning(true);
      return;
    }

    close();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          // The modal container is set as a flexbox to center its child
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Box
          component="div"
          sx={{
            height: "fit-content", // fixed to 50% of the viewport height
            width: "800px",
            borderRadius: "8px",
            bgcolor: "background.paper",
            // Optionally add overflow if the content might be larger than the Box
            overflow: "auto"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: "8px 8px 0px 0px",
              padding: "16px",
              paddingLeft: "32px",
              borderBottom: (theme: Theme) => `1px solid ${theme.outline.main}`
            }}
          >
            <Typography variant="h1">{t("uploadModal.text.title")}</Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                height: "32px",
                borderRadius: "100px"
              }}
            >
              <CloseSvg color="inherit" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box
            sx={{
              height: "60vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              backgroundColor: "white",
              padding: "24px",
              paddingLeft: "32px",
              gap: "16px"
            }}
          >
            {/* Control Bar */}
            <TemplateControlBar />

            <Box sx={{ height: "calc(100% - 52px)" }}>{children}</Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              padding: "16px",
              border: "1px",
              gap: "16px",
              height: "fit-content",
              radiusBottom: "16px",
              width: "100%",
              backgroundColor: "white",
              borderRadius: "0px 0px 8px 8px",
              borderTop: (theme: Theme) => `1px solid ${theme.outline.main}`
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                {numErrors > 0 || numWarnings > 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    <StyledTooltip
                      placement="right"
                      title={<TooltipBodyWithHeader header={t("title.sortCasesByIssues")} body={t("message.sortCasesByIssues")} />}
                    >
                      <Button
                        startIcon={numErrors > 0 ? <ErrorSvg /> : <WarningSvg color={theme.palette.warning.main} />}
                        onClick={() => {
                          store.dispatch(setAreUploadsSorted({ areUploadsSorted: true }));
                        }}
                      >
                        <Typography
                          sx={{
                            textDecoration: "underline",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              fontWeight: 500
                            }
                          }}
                        >
                          {t("uploadModal.text.scansHaveIssues", { count: numErrors > 0 ? numErrors : numWarnings })}
                        </Typography>
                      </Button>
                    </StyledTooltip>
                  </Box>
                ) : (
                  <Box />
                )}
              </Box>
              <LinearProgress variant="determinate" value={50} />
              <Box sx={{ display: "flex", justifyContent: "end", gap: "12px" }}>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: "100px",
                    height: "32px"
                  }}
                  disabled={submitDisabled}
                  onClick={async () => {
                    if (insufficientCreditsForUpload) {
                      setInsufficientCreditsError(true);
                      return;
                    }
                    const state = store.getState();
                    const template = selectTemplate(state);
                    if (orgId && template.templateId !== undefined) {
                      store.dispatch(setModalOpen({ name: UPLOAD_SCANS_MODAL, value: false }));
                      toast.dismiss("addingCases");
                      const caseUploads = selectCaseUploads(state);
                      const scans = selectScans(state);
                      store.dispatch(clearUploadSliceData());
                      await handleUploadCases(
                        {
                          id: template.templateId,
                          caseOptions: TemplateSliceToCaseOptions(template)
                        },
                        orgId,
                        caseUploads,
                        scans
                      );
                    }
                  }}
                >
                  {t("uploadModal.text.processUploads")}
                </Button>
              </Box>
            </Box>
          </Box>
          <ViewportModal />
        </Box>
      </Modal>
      <DialogBox
        title={t("error.noScansDetected")}
        open={noScansDetectedError}
        setOpen={(value) => {
          const detected = typeof value === "boolean" ? value : value(noScansDetectedError);
          store.dispatch(setNoScansDetectedError(detected));
        }}
        args={undefined}
        message={t("message.noScansDetectedHelp")}
        onClose={() => {
          return;
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
        hideDecline
        confirmText={t("common:button.ok")}
      />
      <DialogBox
        title={t("error.emptyFolder")}
        open={emptyFolderError}
        setOpen={(value) => {
          const empty = typeof value === "boolean" ? value : value(emptyFolderError);
          store.dispatch(setEmptyFolderError(empty));
        }}
        args={undefined}
        message={t("message.emptyFolderHelp")}
        onClose={() => {
          return;
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
        hideDecline
        confirmText={t("common:button.ok")}
      />
      <DialogBox
        title={t("title.quitUploadWarning")}
        open={quitWarning}
        setOpen={setQuitWarning}
        args={undefined}
        message={t("message.quitUploadWarning")}
        onClose={(approve) => {
          if (approve) {
            close();
          }
        }}
        sx={{ ".MuiPaper-root": { width: "100%", margin: "16px" } }}
        declineText={t("common:button.cancel")}
        confirmText={t("common:button.discard")}
        confirmColor="error"
      />
      <InsufficientCreditsDialog open={insufficientCreditsError} setOpen={setInsufficientCreditsError} />
      <BasedModelUploadWarningDialog />
    </>
  );
};

export const UPLOAD_SCANS_MODAL = "UploadScans";

export const UploadScansModal = () => {
  const { t } = useTranslation("cases");
  const handleOnFilesAdded = async (files: File[] | DataTransferItem[]) => {
    store.dispatch(setIsUploading(true));

    const loadingId = toast.loading(t("label.addingCases"), { id: AddingCasesToastId });

    // Check if files are of type DataTransferItem[]
    const isDataTransferItemArray = (files: any): files is DataTransferItem[] => {
      return files.every((file: any) => file instanceof DataTransferItem);
    };

    let casesMap: Map<string, DentalCase>;

    const defaultCaseName = t("title.untitled");
    try {
      if (isDataTransferItemArray(files)) {
        // Handling drag event, DataTransferItem[]
        casesMap = await processCaseInputDataTransfer(files, defaultCaseName);
      } else {
        // Handling file input, File[]
        casesMap = await processCaseInputFiles(files, defaultCaseName);
      }
    } catch (error) {
      // These functions only throw these two errors.
      if (error instanceof EmptyError) {
        store.dispatch(setEmptyFolderError(true));
      } else if (error instanceof NoScansDetectedError) {
        store.dispatch(setNoScansDetectedError(true));
      }
      toast.dismiss(loadingId);
      store.dispatch(setIsUploading(false));
      return;
    }

    for (const [name, dentalCase] of casesMap.entries()) {
      if (dentalCase.scans.length === 0) {
        casesMap.delete(name);
      }
    }
    toast.dismiss(loadingId);
    if (casesMap.size === 0) {
      store.dispatch(setIsUploading(false));
      store.dispatch(setNoScansDetectedError(true));
      return;
    }

    let failedUploads = 0;
    let ranOutOfSpace = false;
    await Promise.allSettled(
      casesMap.entries().map(async ([, dentalCase]) => {
        try {
          await store.dispatch(uploadNewCase(dentalCase)).unwrap();
        } catch (err) {
          failedUploads++;
          if ((err as { name?: unknown } | undefined)?.name === "QuotaExceededError") {
            ranOutOfSpace = true;
          }
        }
      })
    );

    if (failedUploads != 0) {
      toast.error(t(ranOutOfSpace ? "toast.failedToLoadCaseRanOutOfSpace" : "toast.failedToLoadCase", { count: failedUploads }));
    }

    store.dispatch(setIsUploading(false));
  };

  return (
    <UploadModal>
      {/* ScansSection */}
      <DndProvider backend={HTML5Backend} context={window}>
        <DragAndDropUpload onFilesAdded={handleOnFilesAdded}>
          <ScansSection handleOnFilesAdded={handleOnFilesAdded} />
        </DragAndDropUpload>
      </DndProvider>
    </UploadModal>
  );
};
